import { useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  extractDitaSourceRefsFromDOM,
  processDitaSourceRefsBatch,
  createDitaLazyLoader,
  classifyDitaResource,
  DitaSourceRef,
} from '../utils/ditaHtmlProcessor';
import { openDitaHtmlViewerTab } from '../utils/commonutils';
import { CtyShowToast, checkType } from '../toast/CtyToast';

interface UseDitaHtmlProcessorOptions {
  getPdfLink: (payload: any) => Promise<any>;
  advisorId?: string; // Required for API calls to include advisor_id
  lazyLoad?: boolean;
  lazyLoadRootMargin?: string;
  concurrencyLimit?: number;
  onError?: (error: Error, sourceId?: string) => void;
  trigger?: any; // Optional trigger to force re-run of processing
}

const RAW_LINK_EXTENSIONS = new Set([
  'pdf', 'png', 'jpg', 'jpeg', 'gif', 'svg', 'webp', 'bmp', 'ico',
  'mp4', 'mp3', 'webm', 'wav', 'mov',
  'xlsx', 'xls', 'docx', 'doc', 'pptx', 'ppt', 'csv', 'zip',
]);

/**
 * React hook to process DITA HTML content
 * 
 * This hook:
 * 1. Extracts all data-dita-source_id references from the container
 * 2. Fetches presigned URLs for each source_id
 * 3. Updates the DOM elements with the presigned URLs
 * 4. Supports lazy loading with IntersectionObserver
 */
export function useDitaHtmlProcessor(
  containerRef: React.RefObject<HTMLElement>,
  options: UseDitaHtmlProcessorOptions
) {
  const {
    getPdfLink,
    advisorId,
    lazyLoad = true,
    lazyLoadRootMargin = '200px',
    concurrencyLimit = 5,
    onError,
    trigger,
  } = options;

  const { t } = useTranslation();
  const observerRef = useRef<IntersectionObserver | null>(null);
  const hasRunOnce = useRef(false);
  const hasWarnedMissingSourceId = useRef(false);

  const processElements = useCallback(
    async (elementsToProcess: HTMLElement[]) => {
      const refsToProcess: DitaSourceRef[] = elementsToProcess
        .map((element) => {
          const sourceId = element.getAttribute('data-dita-source_id');
          const isAlreadyProcessed = element.getAttribute('data-dita-processed') === 'true';
          const type = element.tagName.toLowerCase() === 'img' ? 'img' : 'anchor';
          const originalSrc = element.getAttribute('src') || undefined;
          const originalHref = element.getAttribute('href') || undefined;

          // Only process if has source_id and not already processed in DOM
          if (sourceId && !isAlreadyProcessed) {
            return {
              sourceId,
              element,
              type: type as 'img' | 'anchor',
              originalSrc,
              originalHref,
            };
          }
          return null;
        })
        .filter(Boolean) as DitaSourceRef[];

      if (refsToProcess.length > 0) {
        console.log(`📦 Processing ${refsToProcess.length} elements...`);
        
        // Process them (batch processor will handle deduplication)
        await processDitaSourceRefsBatch(
          refsToProcess,
          getPdfLink,
          advisorId,
          concurrencyLimit,
          onError
        );
      }
    },
    [getPdfLink, advisorId, concurrencyLimit, onError]
  );

  const validateDitaReferences = useCallback(
    (container: HTMLElement) => {
      if (hasWarnedMissingSourceId.current) return;

      let brokenImages = 0;
      let brokenLinks = 0;

      container.querySelectorAll('img, a').forEach((element) => {
        if (classifyDitaResource(element as HTMLElement) !== 'broken-dita') return;
        element.setAttribute('data-dita-error', 'true');
        if (element.tagName.toLowerCase() === 'img') {
          brokenImages += 1;
        } else {
          brokenLinks += 1;
        }
      });

      if (brokenImages === 0 && brokenLinks === 0) return;

      hasWarnedMissingSourceId.current = true;
      const parts: string[] = [];
      if (brokenLinks > 0) {
        parts.push(`${brokenLinks} ${t(brokenLinks === 1 ? 'link' : 'links')}`);
      }
      if (brokenImages > 0) {
        parts.push(`${brokenImages} ${t(brokenImages === 1 ? 'image' : 'images')}`);
      }
      CtyShowToast(
        checkType.error,
        `${parts.join(' ' + t('and') + ' ')} ${t('could not be loaded because the DITA source id is missing.')}`
      );
    },
    [t]
  );

  useEffect(() => {
    console.log('🔄 useDitaHtmlProcessor: Effect triggered', {
      hasContainer: !!containerRef.current,
      hasRunOnce: hasRunOnce.current,
      trigger
    });

    // CRITICAL: Only run ONCE to prevent infinite loops
    if (!containerRef.current || hasRunOnce.current || !trigger) {
      console.log('⏸️ useDitaHtmlProcessor: Skipping', {
        reason: !containerRef.current ? 'no container' : hasRunOnce.current ? 'already ran' : 'trigger not set'
      });
      return;
    }

    const container = containerRef.current;
    
    // Small delay to ensure DOM is fully populated
    const timer = setTimeout(() => {
      // Double-check to prevent race conditions
      if (hasRunOnce.current) {
        console.log('⚠️ useDitaHtmlProcessor: Already ran once, skipping to prevent duplicates');
        return;
      }
      
      console.log('🔍 useDitaHtmlProcessor: Extracting DITA source references (ONE TIME ONLY)...');
      hasRunOnce.current = true;

      validateDitaReferences(container);

      const allSourceRefs = extractDitaSourceRefsFromDOM(container);

      if (allSourceRefs.length === 0) {
        console.warn('⚠️ useDitaHtmlProcessor: No elements with data-dita-source_id found in container');
        return;
      }

      console.log(`📋 useDitaHtmlProcessor: Found ${allSourceRefs.length} source references to process`);

      if (lazyLoad) {
        console.log('🔄 useDitaHtmlProcessor: Setting up lazy loading observer...');
        // Disconnect previous observer if exists
        if (observerRef.current) {
          observerRef.current.disconnect();
        }

        observerRef.current = createDitaLazyLoader((entries) => {
          const visibleElements: HTMLElement[] = [];
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              visibleElements.push(entry.target as HTMLElement);
              observerRef.current?.unobserve(entry.target); // Stop observing once visible
            }
          });
          if (visibleElements.length > 0) {
            console.log(`👁️ useDitaHtmlProcessor: ${visibleElements.length} elements became visible, processing...`);
            processElements(visibleElements);
          }
        }, lazyLoadRootMargin);

        allSourceRefs.forEach((ref) => {
          if (!ref.element.getAttribute('data-dita-processed')) {
            observerRef.current?.observe(ref.element);
          }
        });
        
        console.log('✅ useDitaHtmlProcessor: Lazy loading observer set up successfully');
      } else {
        console.log('🚀 useDitaHtmlProcessor: Processing all elements immediately (lazy load disabled)...');
        // Process all immediately if lazyLoad is false
        processDitaSourceRefsBatch(
          allSourceRefs,
          getPdfLink,
          advisorId,
          concurrencyLimit,
          onError
        );
      }
    }, 200); // Increased delay to ensure DOM is fully rendered

    return () => {
      clearTimeout(timer);
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [
    containerRef,
    getPdfLink,
    lazyLoad,
    lazyLoadRootMargin,
    concurrencyLimit,
    onError,
    processElements,
    validateDitaReferences,
    trigger,
  ]);

  useEffect(() => {
    const handleDitaLinkClick = (event: MouseEvent) => {
      const container = containerRef.current;
      if (!container) return;

      const target = event.target as HTMLElement | null;
      if (!target) return;

      const anchor = target.closest('a') as HTMLAnchorElement | null;
      if (!anchor || !container.contains(anchor)) return;

      if (
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      if ((event as any).__ditaLinkHandled) return;

      const category = classifyDitaResource(anchor);

      if (category === 'external' || category === 'other') {
        return;
      }

      if (category === 'broken-dita') {
        (event as any).__ditaLinkHandled = true;
        event.preventDefault();
        event.stopPropagation();
        CtyShowToast(
          checkType.error,
          t('This link could not be opened because its DITA source id is missing.')
        );
        return;
      }

      const sourceId = anchor.getAttribute('data-dita-source_id') || undefined;
      const resolvedHref = anchor.getAttribute('data-dita-href') || undefined;
      const fileName = anchor.getAttribute('data-dita-filename') || undefined;
      const extension = (anchor.getAttribute('data-dita-extension') || '').toLowerCase();

      if (!sourceId && !resolvedHref) return;

      (event as any).__ditaLinkHandled = true;
      event.preventDefault();
      event.stopPropagation();

      const isRawResource = !!extension && RAW_LINK_EXTENSIONS.has(extension);
      let viewerFileType = 'ditahtml';
      if (isRawResource) {
        viewerFileType = extension === 'pdf' ? 'pdf' : 'ditaresource';
      }

      openDitaHtmlViewerTab({
        sourceId,
        getPdfLink,
        advisorId,
        fileName,
        presignedUrl: resolvedHref,
        fileType: viewerFileType,
      });
    };

    document.addEventListener('click', handleDitaLinkClick, true);
    return () => {
      document.removeEventListener('click', handleDitaLinkClick, true);
    };
  }, [containerRef, getPdfLink, advisorId, t]);
}

